import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { authApi } from '@/api/auth.api';
import { ApiError, refreshSession, setUnauthorizedHandler } from '@/lib/apiClient';
import { queryClient } from '@/lib/queryClient';
import {
  onPlatformConsole, onSessionEvents, publishSessionEnd, requestSessionFromOtherTabs, sessionEndedElsewhere,
} from '@/lib/sessionSync';
import { storage } from '@/lib/storage';
import type { AuthResult, LoginRequest, TenantProfile } from '@/types/auth';
import { useTenantStore } from './TenantStore';

/** Why this tab's session ended, when it ended on its own or in another tab — for the login page's notice. */
export type SessionEndReason = 'logout' | 'expired';

/**
 * Authentication (session) store — owns the signed-in tenant user's profile and the
 * login/logout lifecycle. Tokens themselves live in `storage`; the axios layer reads them
 * and performs refresh-token rotation.
 *
 * <p>
 * Three states, never two: <b>checking</b> (initializing — nothing protected renders), <b>signed in</b>
 * (a profile the SERVER returned for this session — a token merely being present proves nothing) and
 * <b>signed out</b>. Every tab of the browser shares the one session (see lib/sessionSync): a sign-in,
 * a sign-out or an expiry in any tab reaches all of them.
 * </p>
 */
interface SessionStoreValue {
  profile: TenantProfile | null;
  isAuthenticated: boolean;
  initializing: boolean;
  endReason: SessionEndReason | null;
  /** Signs in; resolves to true when the user must change a temporary password before continuing. */
  login: (req: LoginRequest) => Promise<boolean>;
  /** Starts a session from tokens already issued (support sign-in with the company's consent). */
  adopt: (result: AuthResult) => Promise<void>;
  logout: () => Promise<void>;
}

const SessionContext = createContext<SessionStoreValue | undefined>(undefined);

/** A refusal (as opposed to no network or a server error) means the session itself is gone. */
function isAuthRefusal(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 401 || error.status === 403);
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const { setTenantCode } = useTenantStore();
  const [profile, setProfile] = useState<TenantProfile | null>(null);
  const [initializing, setInitializing] = useState(true);
  const [endReason, setEndReason] = useState<SessionEndReason | null>(null);
  // Read by the cross-tab handler, which is registered once and must see the current profile.
  const profileRef = useRef<TenantProfile | null>(null);
  useEffect(() => { profileRef.current = profile; }, [profile]);

  // Everything this browser holds for the session: the tokens, the signed-in user, and every cached
  // response — a cached customer list must not be one Back press away after signing out.
  const clear = useCallback(() => {
    storage.clearSession();
    try { window.sessionStorage.removeItem('travelerp.supportSession'); } catch { /* private mode */ }
    setProfile(null);
    queryClient.clear();
  }, []);

  /** The session ended in THIS tab: clear it here, and tell every other tab. */
  const end = useCallback((reason: SessionEndReason) => {
    clear();
    // The tab somebody pressed Sign out in needs no notice; an expiry does.
    setEndReason(reason === 'expired' ? 'expired' : null);
    publishSessionEnd(reason === 'expired' ? 'SESSION_EXPIRED' : 'LOGOUT');
  }, [clear]);

  // The API client gave up on a session it could not renew: expired, in every tab.
  useEffect(() => {
    setUnauthorizedHandler(() => end('expired'));
  }, [end]);

  // What the other tabs of this browser do to the shared session.
  useEffect(() => onSessionEvents({
    // Signed out, or expired, elsewhere: the stored session is already gone. Dropping the profile
    // sends every protected page in this tab to the login page at once.
    ended: (type) => {
      const hadSession = profileRef.current !== null;
      setProfile(null);
      queryClient.clear();
      // The notice is for somebody who WAS signed in here; a tab already on the login page needs none.
      if (hadSession) setEndReason(type === 'SESSION_EXPIRED' ? 'expired' : 'logout');
    },
    // Signed in elsewhere (or somebody else did): load who this session belongs to now.
    changed: () => {
      authApi.profile()
        .then((p) => { setTenantCode(p.tenantCode); setEndReason(null); setProfile(p); })
        .catch((e) => { if (isAuthRefusal(e)) clear(); });
    },
  }), [clear, setTenantCode]);

  // Start-up: validate the session with the server before anything protected renders.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        // The Platform Console has its own sign-in; the tenant session is not checked there.
        if (onPlatformConsole()) return;

        // Ended while this tab was asleep, closed or reloading: over, whatever storage still holds.
        if (sessionEndedElsewhere()) storage.clearSession();

        // Nothing here: another open tab may hold the session.
        if (!storage.accessToken && !storage.refreshToken) await requestSessionFromOtherTabs();

        // A refresh token alone still restores the session.
        if (!storage.accessToken && storage.refreshToken) await refreshSession();

        if (!storage.accessToken) return;

        // The server decides. An expired access token is renewed on the way (the 401 interceptor);
        // a refresh token that is also dead ends the session and every tab is told.
        const p = await authApi.profile();
        if (active) {
          setTenantCode(p.tenantCode);
          setProfile(p);
        }
      } catch (e) {
        // Refused: the session is gone. No network or a server error leaves the tokens alone — a
        // blip at start-up must not throw away a session that is perfectly good.
        if (isAuthRefusal(e)) clear();
      } finally {
        if (active) setInitializing(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [clear, setTenantCode]);

  // Coming back to a tab — shown again, or restored by Back from the browser's page cache — checks
  // what storage says before anything protected is used again. Event-driven, never polled.
  useEffect(() => {
    if (!profile) return undefined;

    const recheck = () => {
      if (onPlatformConsole()) return;
      if (sessionEndedElsewhere() || (!storage.accessToken && !storage.refreshToken)) {
        clear();
        setEndReason('logout');
      }
    };
    const onVisible = () => { if (document.visibilityState === 'visible') recheck(); };
    const onShow = (e: PageTransitionEvent) => { if (e.persisted) recheck(); };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('pageshow', onShow);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('pageshow', onShow);
    };
  }, [profile, clear]);

  const login = useCallback(
    async (req: LoginRequest) => {
      // Email + Password login: clear any stale tenant from a previous session so the backend
      // resolves the tenant from the credentials (email-based discovery) / request host — never a
      // wrong X-Tenant header. An explicit tenantCode (internal callers) is still honored.
      setTenantCode(req.tenantCode ?? null);
      const result = await authApi.login(req);
      // Written, and handed to every other open tab of this browser.
      storage.setSession(result);
      const p = await authApi.profile(); // /auth/profile is allowed even while a change is pending
      setTenantCode(p.tenantCode);
      setEndReason(null);
      setProfile(p);
      return result.mustChangePassword === true;
    },
    [setTenantCode],
  );

  const adopt = useCallback(
    async (result: AuthResult) => {
      setTenantCode(null);
      storage.setSession(result);
      const p = await authApi.profile();
      setTenantCode(p.tenantCode);
      setEndReason(null);
      setProfile(p);
    },
    [setTenantCode],
  );

  const logout = useCallback(async () => {
    const token = storage.refreshToken;
    // Revoked on the server first: after this the refresh token cannot mint another access token.
    try {
      if (token) await authApi.logout(token);
    } catch {
      /* best-effort — the tokens leave this browser either way */
    }
    // Signing out signs this whole browser out — every tab, not only the one the button was in.
    end('logout');
  }, [end]);

  const value = useMemo<SessionStoreValue>(
    () => ({ profile, isAuthenticated: !!profile, initializing, endReason, login, adopt, logout }),
    [profile, initializing, endReason, login, adopt, logout],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSessionStore() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSessionStore must be used within a SessionProvider');
  return ctx;
}

